import React, {useEffect, useState} from 'react';
import {
  ActivityIndicator,
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';

import {BookOpen, Crown, Info, MessageSquare, Mic, X} from 'components/Icons';
import {cardAction, managedElsewhereNote, minutes, money, offerFor} from 'utils/billing/billingModel';
import BuyMoreUsagePanel from './BuyMoreUsagePanel';

const GREEN = '#C5E836';
const REASON_ICONS = {voice: Mic, chat: MessageSquare, course: BookOpen, paid: Crown};

// The portal's METERS: short labels, the explanation behind an info tip (owner 2026-09-29).
const METERS = [
  {key: 'voice', label: 'Voice minutes', tip: 'Talk with your tutor and hear replies.', amount: plan => minutes(plan.allowances?.voiceSeconds)},
  {key: 'text', label: 'Chat minutes', tip: 'Type or talk to your tutor; replies are written.', amount: plan => minutes(plan.allowances?.textSeconds)},
  {key: 'course', label: 'Course generations', tip: 'Courses you create on any topic.', amount: plan => plan.allowances?.generatedCourses || 0},
];

function MeterRow({meter, plan, period}) {
  const [tip, setTip] = useState(false);
  return (
    <View style={styles.meterRow}>
      <View style={styles.meterLine}>
        <View style={styles.meterName}>
          <Text style={styles.meterLabel}>{meter.label}</Text>
          <Pressable
            accessibilityLabel={`About ${meter.label}`}
            onPress={() => setTip(open => !open)}
            onHoverIn={() => setTip(true)}
            onHoverOut={() => setTip(false)}
            style={styles.info}>
            <Info size={13} color="#A1A1AA" />
          </Pressable>
        </View>
        <Text style={styles.meterAmount}>
          {meter.amount(plan).toLocaleString()}
          {period === 'annual' ? <Text style={styles.perMonth}>/mo</Text> : null}
        </Text>
      </View>
      {tip ? <Text style={styles.tip}>{meter.tip}</Text> : null}
    </View>
  );
}

function PeriodToggle({period, onChange}) {
  const option = (value, label) => (
    <TouchableOpacity
      accessibilityRole="radio"
      accessibilityState={{checked: period === value}}
      style={[styles.toggleOption, period === value && styles.toggleOn]}
      onPress={() => onChange(value)}>
      <Text style={[styles.toggleText, period === value && styles.toggleTextOn]}>{label}</Text>
    </TouchableOpacity>
  );
  return (
    <View style={styles.toggleWrap}>
      <View style={styles.toggle}>
        {option('monthly', 'Monthly')}
        {option('annual', 'Annually')}
      </View>
      <Text style={styles.toggleNote}>{period === 'annual' ? 'You get 2 months free' : 'Choose annually to get 2 months free'}</Text>
    </View>
  );
}

function PlanCard({plan, period, current, currentInterval, scheduled, isFree, recommended, busy, managed, onCheckout, onChange, narrow}) {
  const isCurrent = current?.planKey === plan.planKey;
  const offer = offerFor(plan, period);
  const action = cardAction({plan, period, current, currentInterval, scheduled, isFree});
  const strong = isCurrent || recommended;
  let button;
  if (action.kind === 'checkout' || action.kind === 'change') {
    button = managed ? null : (
      <TouchableOpacity
        style={[styles.cardButton, busy && styles.disabled]}
        disabled={busy}
        onPress={() => (action.kind === 'change' ? onChange(action.offerId) : onCheckout(action.offerId))}>
        <Text style={styles.cardButtonText}>{action.label}</Text>
      </TouchableOpacity>
    );
  } else if (action.kind === 'unavailable') {
    button = <Text style={styles.unavailable}>{action.label}</Text>;
  } else {
    button = (
      <View style={styles.cardBadge}>
        <Text style={[styles.cardBadgeText, action.kind === 'current' && {color: GREEN}]}>{action.label}</Text>
      </View>
    );
  }
  return (
    <View style={[styles.card, narrow ? styles.cardNarrow : styles.cardWide, strong && styles.cardStrong]}>
      {recommended ? (
        <View style={styles.recommended}>
          <Text style={styles.recommendedText}>RECOMMENDED</Text>
        </View>
      ) : null}
      <View style={[styles.cardHead, isCurrent && styles.cardHeadCurrent]}>
        <Text style={[styles.cardName, isCurrent && styles.cardNameCurrent]}>{String(plan.name).toUpperCase()}</Text>
        {isCurrent ? (
          <View style={styles.currentChip}>
            <Text style={styles.currentChipText}>CURRENT</Text>
          </View>
        ) : scheduled?.planKey === plan.planKey ? (
          <View style={styles.nextChip}>
            <Text style={styles.nextChipText}>NEXT</Text>
          </View>
        ) : null}
      </View>
      <View style={styles.cardBody}>
        <Text style={styles.price}>
          {offer ? money(offer.amountMinor, plan.currency) : money(0, plan.currency)}
          <Text style={styles.per}>/{offer?.billingInterval === 'annual' ? 'yr' : 'mo'}</Text>
        </Text>
        {/* Allowances reset monthly on every plan, so a yearly price shows "/mo" on each figure (Sol topup-per-meter 2 B). */}
        {METERS.map(meter => <MeterRow key={meter.key} meter={meter} plan={plan} period={period} />)}
        <View style={styles.cardAction}>{button}</View>
      </View>
    </View>
  );
}

/**
 * The full-screen plans in the world (owner 2026-09-29; Sol build-plan D7): what opened them in the
 * two-pattern wording, the learner's plan, one Monthly/Annually toggle and the three paid plans in a
 * row with the middle one Recommended. Lower paid plans get a small "Buy more usage" link; the top
 * plan, with nowhere to upgrade, gets "Buy more usage" itself. Nothing here leaves the world except
 * Stripe Checkout, which comes straight back.
 */
export default function WorldPlans({
  visible,
  reason,
  plans,
  subscription,
  isFree,
  managedBy,
  busy,
  loading,
  onClose,
  onDismiss,
  onCheckout,
  onChange,
  onBuyMore,
  children,
}) {
  const {width} = useWindowDimensions();
  const narrow = width < 820;
  const paid = (plans || []).filter(plan => plan.planKey !== 'free').sort((a, b) => a.monthlyPriceMinor - b.monthlyPriceMinor);
  const top = paid[paid.length - 1];
  const recommended = paid.length >= 3 ? paid[Math.floor(paid.length / 2)].planKey : null;
  const currentPlan = isFree ? null : paid.find(plan => plan.planKey === subscription?.planKey) || null;
  const currentInterval = isFree ? null : subscription?.billingInterval || null;
  const onTopPlan = Boolean(top && currentPlan?.planKey === top.planKey);
  const pending = isFree ? null : subscription?.pendingChange || null;
  const managed = managedBy && managedBy !== 'stripe' ? managedBy : null;
  const [period, setPeriod] = useState(currentInterval || 'monthly');
  useEffect(() => {
    if (visible) setPeriod(currentInterval || 'monthly');
  }, [visible, currentInterval]);
  const ReasonIcon = REASON_ICONS[reason?.icon || 'paid'];

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.screen}>
        <ScrollView contentContainerStyle={styles.scroll}>
          {reason ? (
            <View style={styles.reason}>
              <View style={[styles.reasonIcon, reason.tone === 'paid' ? styles.reasonPaid : styles.reasonOut]}>
                <ReasonIcon size={24} color={reason.tone === 'paid' ? GREEN : '#FCD34D'} />
              </View>
              <Text style={styles.title}>{reason.title}</Text>
              {reason.line ? <Text style={styles.line}>{reason.line}</Text> : null}
            </View>
          ) : (
            <Text style={styles.title}>{isFree ? 'Upgrade your plan' : 'Change your plan'}</Text>
          )}

          {isFree || (onTopPlan && reason) ? (
            <View style={styles.yourPlan}>
              <Text style={styles.yourPlanText}>
                Your plan: <Text style={styles.yourPlanName}>{currentPlan?.name || 'Free'}</Text>
              </Text>
              {isFree ? (
                <View style={styles.limited}>
                  <Text style={styles.limitedText}>LIMITED</Text>
                </View>
              ) : null}
            </View>
          ) : null}

          {onTopPlan && reason ? (
            <View style={styles.buyMoreCard}>
              <Text style={styles.buyMoreTitle}>Buy more usage</Text>
              <BuyMoreUsagePanel />
            </View>
          ) : (
            <>
              <PeriodToggle period={period} onChange={setPeriod} />
              {managed ? <Text style={styles.managed}>{managedElsewhereNote(managed)}</Text> : null}
              {paid.length && !loading ? (
                <View style={[styles.cards, narrow && styles.cardsNarrow]}>
                  {paid.map(plan => (
                    <PlanCard
                      key={plan.planKey}
                      plan={plan}
                      period={period}
                      current={currentPlan}
                      currentInterval={currentInterval}
                      scheduled={pending}
                      isFree={isFree}
                      recommended={recommended === plan.planKey}
                      busy={busy}
                      managed={Boolean(managed)}
                      onCheckout={onCheckout}
                      onChange={onChange}
                      narrow={narrow}
                    />
                  ))}
                </View>
              ) : (
                <ActivityIndicator style={styles.loading} color={GREEN} />
              )}
              {!isFree ? (
                <TouchableOpacity onPress={onBuyMore} style={styles.linkButton}>
                  <Text style={styles.link}>Buy more usage</Text>
                </TouchableOpacity>
              ) : null}
            </>
          )}
          <TouchableOpacity onPress={onDismiss || onClose} style={styles.linkButton}>
            <Text style={styles.dismiss}>{reason?.dismissLabel || 'Not now'}</Text>
          </TouchableOpacity>
        </ScrollView>
        <TouchableOpacity accessibilityLabel="Close" style={styles.close} onPress={onClose}>
          <X size={24} color="#A1A1AA" />
        </TouchableOpacity>
        {children}
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  screen: {flex: 1, backgroundColor: 'rgba(0,0,0,0.92)'},
  scroll: {flexGrow: 1, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 16, paddingVertical: 48},
  close: {position: 'absolute', top: 18, right: 18, padding: 8, borderRadius: 999},
  reason: {alignItems: 'center'},
  reasonIcon: {width: 48, height: 48, borderRadius: 24, alignItems: 'center', justifyContent: 'center', marginBottom: 12},
  reasonPaid: {backgroundColor: 'rgba(197,232,54,0.15)'},
  reasonOut: {backgroundColor: 'rgba(252,211,77,0.15)'},
  title: {color: '#F6FBFF', fontSize: 24, fontWeight: '700', textAlign: 'center'},
  line: {marginTop: 4, color: '#A1A1AA', fontSize: 14, textAlign: 'center'},
  yourPlan: {marginTop: 16, flexDirection: 'row', alignItems: 'center', paddingVertical: 4, paddingHorizontal: 12, borderRadius: 999, borderWidth: 1, borderColor: 'rgba(255,255,255,0.1)'},
  yourPlanText: {color: '#D4D4D8', fontSize: 12},
  yourPlanName: {color: '#F6FBFF', fontWeight: '700'},
  limited: {marginLeft: 8, paddingVertical: 2, paddingHorizontal: 8, borderRadius: 999, backgroundColor: 'rgba(252,211,77,0.15)'},
  limitedText: {color: '#FDE68A', fontSize: 10, fontWeight: '700'},
  toggleWrap: {alignItems: 'center', marginTop: 24, marginBottom: 32},
  toggle: {flexDirection: 'row', padding: 4, borderRadius: 999, borderWidth: 1, borderColor: 'rgba(255,255,255,0.15)', backgroundColor: 'rgba(0,0,0,0.4)'},
  toggleOption: {paddingVertical: 6, paddingHorizontal: 20, borderRadius: 999},
  toggleOn: {backgroundColor: GREEN},
  toggleText: {color: '#D4D4D8', fontSize: 14, fontWeight: '600'},
  toggleTextOn: {color: '#10151F'},
  toggleNote: {marginTop: 8, color: GREEN, fontSize: 12, fontWeight: '500'},
  managed: {marginBottom: 16, paddingVertical: 8, paddingHorizontal: 16, borderRadius: 8, borderWidth: 1, borderColor: 'rgba(255,255,255,0.1)', color: '#D4D4D8', fontSize: 14},
  cards: {width: '100%', maxWidth: 1000, flexDirection: 'row', alignItems: 'stretch', justifyContent: 'center'},
  cardsNarrow: {flexDirection: 'column', alignItems: 'center'},
  card: {borderRadius: 12, borderWidth: 1, borderColor: 'rgba(197,232,54,0.7)', backgroundColor: 'rgba(0,0,0,0.4)'},
  cardWide: {flex: 1, marginHorizontal: 10},
  cardNarrow: {width: '100%', maxWidth: 420, marginVertical: 12},
  cardStrong: {borderColor: GREEN, borderWidth: 2, shadowColor: GREEN, shadowOpacity: 0.25, shadowRadius: 16, shadowOffset: {width: 0, height: 0}},
  recommended: {position: 'absolute', top: -12, alignSelf: 'center', zIndex: 2, paddingVertical: 3, paddingHorizontal: 12, borderRadius: 999, backgroundColor: GREEN},
  recommendedText: {color: '#10151F', fontSize: 11, fontWeight: '800', letterSpacing: 0.6},
  cardHead: {paddingVertical: 14, paddingHorizontal: 16, alignItems: 'center', borderTopLeftRadius: 10, borderTopRightRadius: 10, backgroundColor: '#000'},
  cardHeadCurrent: {backgroundColor: GREEN},
  cardName: {color: '#F6FBFF', fontSize: 18, fontWeight: '800', letterSpacing: 0.6},
  cardNameCurrent: {color: '#10151F'},
  currentChip: {position: 'absolute', right: 12, top: 15, paddingVertical: 2, paddingHorizontal: 8, borderRadius: 999, backgroundColor: 'rgba(0,0,0,0.15)'},
  currentChipText: {color: '#10151F', fontSize: 10, fontWeight: '700'},
  nextChip: {position: 'absolute', right: 12, top: 15, paddingVertical: 2, paddingHorizontal: 8, borderRadius: 999, backgroundColor: 'rgba(197,232,54,0.15)'},
  nextChipText: {color: GREEN, fontSize: 10, fontWeight: '700'},
  cardBody: {flex: 1, padding: 16},
  price: {marginBottom: 16, textAlign: 'center', color: '#F6FBFF', fontSize: 30, fontWeight: '800'},
  per: {color: '#A1A1AA', fontSize: 14, fontWeight: '400'},
  meterRow: {marginBottom: 10},
  meterLine: {flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between'},
  meterName: {flexDirection: 'row', alignItems: 'center'},
  meterLabel: {color: '#D4D4D8', fontSize: 14},
  info: {marginLeft: 6, padding: 2},
  meterAmount: {color: '#F6FBFF', fontSize: 14, fontWeight: '700'},
  perMonth: {color: '#A1A1AA', fontSize: 12, fontWeight: '400'},
  tip: {marginTop: 4, color: '#A1A1AA', fontSize: 12},
  cardAction: {marginTop: 'auto', paddingTop: 20},
  cardButton: {paddingVertical: 11, borderRadius: 8, alignItems: 'center', backgroundColor: GREEN},
  cardButtonText: {color: '#10151F', fontSize: 14, fontWeight: '800'},
  cardBadge: {paddingVertical: 10, borderRadius: 8, alignItems: 'center', borderWidth: 1, borderColor: 'rgba(197,232,54,0.5)'},
  cardBadgeText: {color: '#E4E4E7', fontSize: 14, fontWeight: '600'},
  unavailable: {paddingVertical: 10, textAlign: 'center', color: '#71717A', fontSize: 12},
  disabled: {opacity: 0.5},
  loading: {marginVertical: 40},
  buyMoreCard: {marginTop: 32, width: '100%', maxWidth: 448, padding: 24, borderRadius: 16, borderWidth: 1, borderColor: 'rgba(255,255,255,0.1)', backgroundColor: '#0D1117'},
  buyMoreTitle: {marginBottom: 20, textAlign: 'center', color: '#F6FBFF', fontSize: 18, fontWeight: '700'},
  linkButton: {marginTop: 20, padding: 4},
  link: {color: GREEN, fontSize: 14, fontWeight: '600'},
  dismiss: {color: '#A1A1AA', fontSize: 14, fontWeight: '600'},
});
